import { useEffect, useRef, useState } from "react";

import { Bell } from "lucide-react";

import {
  useListInAppNotificationsQuery,
  useMarkAllInAppNotificationsReadMutation,
  useMarkInAppNotificationReadMutation,
} from "@/features/notifications/api/inAppNotificationsApi";
import { usePendingCountsQuery } from "@/features/notifications/api/pendingCountsApi";
import { useLiveInAppNotifications } from "@/features/notifications/hooks/useLiveInAppNotifications";
import { NotificationPanel } from "@/features/notifications/ui/NotificationPanel";
import type { NotificationPanelTab } from "@/features/notifications/ui/NotificationPanelTabs";

export function NotificationBell() {
  useLiveInAppNotifications();
  const { data: pending } = usePendingCountsQuery(undefined, { pollingInterval: 60_000 });
  const { data: feed, refetch: refetchInApp } = useListInAppNotificationsQuery(undefined, {
    pollingInterval: 60_000,
  });
  const [markRead] = useMarkInAppNotificationReadMutation();
  const [markAllRead] = useMarkAllInAppNotificationsReadMutation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationPanelTab>("all");
  const ref = useRef<HTMLDivElement>(null);
  const inApp = feed?.results ?? [];
  const unread = feed?.unread_count ?? 0;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) void refetchInApp();
  }, [open, refetchInApp]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell-toggle"
        className="relative rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${unread} unread notifications`}
      >
        <Bell className="size-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-0.5 text-3xs font-bold text-primary-foreground">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open ? (
        <NotificationPanel
          tab={tab}
          onTabChange={setTab}
          pending={pending}
          inApp={inApp}
          unread={unread}
          onMarkAllRead={() => void markAllRead()}
          onClose={() => setOpen(false)}
          onOpenRow={(id, readAt) => {
            if (!readAt) void markRead(id);
          }}
        />
      ) : null}
    </div>
  );
}
